import React, { useCallback, useEffect, useState } from "react";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import OrdonnanceModal from "../../components/ordonnance/OrdonnanceModal";
import CreateCommandeModal from "../../components/CreateCommandeModal";
import { migrateLegacyDraftBackups } from "../save/draftBackup";
import { useAuth } from "../../auth/AuthContext";
import { CommandeDetailView } from "./components/CommandeDetailView";
import { CommandeListView } from "./components/CommandeListView";
import { CommandeMobileFiltersLayer, CommandeMobileLabelsLayer } from "./components/CommandeMobileLayers";
import {
    CommandeValidationModal,
    DeleteMailConfirmDialog,
    PatientIdentityConflictDialog,
    PendingDeleteToasts,
    SavedToast,
} from "./components/CommandePageDialogs";
import { useCommandeDetailLoader, useCommandeNavigation } from "./hooks/useCommandeDetail";
import { useCommandeDraft, useCommandeLockState, useCommandeSaving } from "./hooks/useCommandeDraft";
import { useCommandeFilters, useNavigationSearch } from "./hooks/useCommandeFilters";
import { useCommandeList } from "./hooks/useCommandeList";
import { useCommandePreview, useDetailDeletion, useLockEvents } from "./hooks/useCommandePageEvents";
import { EMPTY_CONTEXT } from "./model/commandeConstants";
import { validatePatients } from "./model/commandeValidation";

export { computeProductionCommentPatches } from "./model/commandePatches";

// Ouverture de la commande sélectionnée, vue courante et fenêtres de la page.
function useCommandeSelection() {
    const [view, setView] = useState("list");
    const [selectedCommande, setSelectedCommande] = useState(null);
    const [readOverrides, setReadOverrides] = useState({});
    const [selectedOrdonnance, setSelectedOrdonnance] = useState(null);
    const [pendingCommand, setPendingCommand] = useState(null);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [savedToast, setSavedToast] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [labelsOpen, setLabelsOpen] = useState(false);
    const markRead = useCallback(id => setReadOverrides(prev => ({ ...prev, [id]: true })), []);
    const openDetail = useCallback(cmd => {
        setSelectedCommande(cmd);
        setView("detail");
    }, []);
    return {
        view, setView, selectedCommande, setSelectedCommande, readOverrides, markRead, openDetail,
        selectedOrdonnance, setSelectedOrdonnance, pendingCommand, setPendingCommand, showValidationModal,
        setShowValidationModal, showCreateModal, setShowCreateModal, savedToast, setSavedToast,
        filtersOpen, setFiltersOpen, labelsOpen, setLabelsOpen,
    };
}

// Boîte des commandes d'un écran (gestion, validation, robot, étiquetage, matériel médical…) : liste filtrée,
// aperçu, détail avec verrou d'édition, sauvegarde automatique et suppression annulable. `children` reçoit le
// brouillon pour afficher l'éditeur propre à l'écran.
export default function CommandePage({ context = EMPTY_CONTEXT, children, renderDetailActions }) {
    const { user, pharmacyId: currentPharmacyId } = useAuth();
    const { isMobile, isTablet } = useBreakpoint();
    const isOverlay = isMobile || isTablet;
    const selection = useCommandeSelection();
    const { view, selectedCommande, markRead, openDetail } = selection;
    const f = useCommandeFilters(context);
    useNavigationSearch(f, commandeId => {
        markRead(commandeId);
        openDetail({ id: commandeId });
    });
    useEffect(() => {
        // Reprise non destructive des brouillons laissés dans le navigateur par les versions précédentes.
        migrateLegacyDraftBackups();
    }, []);

    const list = useCommandeList({ f, user, view, context });
    const lock = useCommandeLockState(selectedCommande, user);
    const d = useCommandeDraft({ context, list });
    const saving = useCommandeSaving({ d, list, lock, view });
    useCommandeDetailLoader({ selectedCommande, selectedCommandeRef: lock.selectedCommandeRef, d, list, context });
    const preview = useCommandePreview({ view, isMobile, context });
    const nav = useCommandeNavigation({ d, list, saving, selection: { ...selection, setPreviewCommande: preview.setPreviewCommande } });
    const deletion = useDetailDeletion({ goToList: nav.goToList, setCommandes: list.setCommandes, refresh: list.refresh });
    useLockEvents({ currentPharmacyId, list, lock });

    const handleSelectCommande = useCallback((cmd) => {
        deletion.flushPendingDetailDeletes(false);
        preview.setPreviewCommande(cmd);
        markRead(cmd.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletion.flushPendingDetailDeletes, markRead]);

    // Changer de commande enregistre d'abord la commande ouverte ; des champs incomplets demandent confirmation.
    const handleEditCommande = async (cmd) => {
        deletion.flushPendingDetailDeletes(false);
        markRead(cmd.id);
        if (!d.draft?.id) {
            openDetail(cmd);
            return;
        }
        if (validatePatients(d.draft.patients || []).length > 0) {
            selection.setPendingCommand(cmd);
            selection.setShowValidationModal(true);
            return;
        }
        if (await saving.commitDraft()) openDetail(cmd);
    };

    const vm = {
        ...selection, ...f, ...list, ...lock, ...d, ...saving, ...nav, ...deletion, ...preview,
        context, children, renderDetailActions, isMobile, isOverlay, currentPharmacyId,
        handleSelectCommande, handleEditCommande,
    };
    const { filtersOpen, labelsOpen } = selection;

    return (
        <div style={{ display: "flex", height: "100%", minHeight: 0, overflow: "hidden" }}>
            {view === "list" && isOverlay && context?.showListFilters !== false && filtersOpen && <CommandeMobileFiltersLayer vm={vm} />}
            {view === "list" && isOverlay && labelsOpen && <CommandeMobileLabelsLayer vm={vm} />}

            <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {view === "list" ? <CommandeListView vm={vm} /> : <CommandeDetailView vm={vm} />}
            </div>

            <CommandePageOverlays vm={vm} />
        </div>
    );
}

// Fenêtres et messages par-dessus la page : confirmations, conflit patient, ordonnance, création et toasts.
function CommandePageOverlays({ vm }) {
    const {
        showValidationModal, patientIdentityConflict, deleteConfirmationCommand, selectedOrdonnance, setSelectedOrdonnance,
        draft, updateDraft, context, commandeDetail, save, isDirty, showCreateModal, setShowCreateModal,
        handleCommandeCreated, savedToast, pendingDeleteDetails,
    } = vm;
    return (
        <>
            {showValidationModal && <CommandeValidationModal vm={vm} />}
            {patientIdentityConflict && <PatientIdentityConflictDialog vm={vm} />}
            {deleteConfirmationCommand && <DeleteMailConfirmDialog vm={vm} />}
            {selectedOrdonnance && (
                <OrdonnanceModal
                    ordonnance={selectedOrdonnance}
                    draft={draft}
                    setDraft={updateDraft}
                    onClose={() => setSelectedOrdonnance(null)}
                    medicamentStrategy={context?.medicamentStrategy}
                    commande={commandeDetail}
                    onSave={save}
                    isDirty={isDirty}
                />
            )}

            {showCreateModal && (
                <CreateCommandeModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={handleCommandeCreated}
                    medicamentStrategy={context?.medicamentStrategy}
                    serviceKey={context?.serviceKey}
                />
            )}

            {savedToast && <SavedToast vm={vm} />}
            {pendingDeleteDetails.length > 0 && <PendingDeleteToasts vm={vm} />}
        </>
    );
}
